import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { salesPlaybookApi } from '../../services/apiService';

export type SalesPlaybookTag = 'situation' | 'pain' | 'cost_math' | 'authority' | 'timeline' | 'close_trigger';

export interface SalesPlaybookQuestion {
  _id: string;
  questionText: string;
  tags: SalesPlaybookTag[];
  sortOrder: number;
  isActive: boolean;
}

export interface SalesPlaybookRoleInfo {
  name: string;
  displayName: string;
}

export interface SalesPlaybookIndustryInfo {
  industry: string;
  displayName: string;
  roles: SalesPlaybookRoleInfo[];
}

export interface RoleData {
  industry: string;
  industryDisplayName: string;
  roleId: string;
  roleName: string;
  roleDisplayName: string;
  pitch: string[];
  advanced: string[];
  questions: SalesPlaybookQuestion[];
}

interface ItemNote {
  note: string;
  timestamp: string;
}

// Backward compatibility alias
type QuestionNote = ItemNote;

// Tracking data stored per industry+role combination
interface SelectionTrackingData {
  askedQuestions: string[];
  questionNotes: Record<string, ItemNote>;
  checkedPitches: string[];
  pitchNotes: Record<string, ItemNote>;
  checkedAdvanced: string[];
  advancedNotes: Record<string, ItemNote>;
}

interface SalesPlaybookState {
  // Available industries (for dropdown)
  industries: SalesPlaybookIndustryInfo[];
  industriesLoading: boolean;

  // Current selection
  selectedIndustry: string | null;
  selectedIndustryDisplayName: string | null;
  selectedRole: string;
  selectedRoleDisplayName: string | null;

  // Current role's data (pitch, advanced, and questions for selected role)
  currentPitch: string[];
  currentAdvanced: string[];
  availableRoles: SalesPlaybookRoleInfo[];
  questions: SalesPlaybookQuestion[];
  dataLoading: boolean;

  // Live call tracking (for current selection)
  askedQuestions: string[]; // Question IDs marked as asked
  questionNotes: Record<string, ItemNote>;
  checkedPitches: string[]; // Pitch bullet IDs (using index as ID)
  pitchNotes: Record<string, ItemNote>;
  checkedAdvanced: string[]; // Advanced bullet IDs (using index as ID)
  advancedNotes: Record<string, ItemNote>;

  // Cache of tracking data per industry+role (key: "industry|role")
  trackingCache: Record<string, SelectionTrackingData>;

  error: string | null;
}

// Helper to create cache key
const getTrackingCacheKey = (industry: string, role: string): string => `${industry}|${role}`;

const initialState: SalesPlaybookState = {
  industries: [],
  industriesLoading: false,
  selectedIndustry: null,
  selectedIndustryDisplayName: null,
  selectedRole: 'general',
  selectedRoleDisplayName: 'General',
  currentPitch: [],
  currentAdvanced: [],
  availableRoles: [],
  questions: [],
  dataLoading: false,
  askedQuestions: [],
  questionNotes: {},
  checkedPitches: [],
  pitchNotes: {},
  checkedAdvanced: [],
  advancedNotes: {},
  trackingCache: {},
  error: null
};

// Async thunks
export const fetchIndustries = createAsyncThunk(
  'salesPlaybook/fetchIndustries',
  async (_, { rejectWithValue }) => {
    try {
      const response = await salesPlaybookApi.getIndustries();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch industries');
    }
  }
);

export const fetchRoleData = createAsyncThunk<
  RoleData | null,
  { industry: string; roleName: string },
  { rejectValue: string }
>(
  'salesPlaybook/fetchRoleData',
  async ({ industry, roleName }, { rejectWithValue }) => {
    try {
      const response = await salesPlaybookApi.getRoleData(industry, roleName);
      return response as RoleData;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch role data');
    }
  }
);

const salesPlaybookSlice = createSlice({
  name: 'salesPlaybook',
  initialState,
  reducers: {
    setSelectedIndustry: (state, action: PayloadAction<{ industry: string; displayName: string; roles: SalesPlaybookRoleInfo[] } | null>) => {
      // Always save current role's tracking data to cache before switching
      if (state.selectedIndustry && state.selectedRole) {
        const currentKey = getTrackingCacheKey(state.selectedIndustry, state.selectedRole);
        // Deep copy all notes to avoid reference issues
        const questionNotesCopy: Record<string, ItemNote> = {};
        Object.keys(state.questionNotes).forEach(key => {
          questionNotesCopy[key] = { ...state.questionNotes[key] };
        });
        const pitchNotesCopy: Record<string, ItemNote> = {};
        Object.keys(state.pitchNotes).forEach(key => {
          pitchNotesCopy[key] = { ...state.pitchNotes[key] };
        });
        const advancedNotesCopy: Record<string, ItemNote> = {};
        Object.keys(state.advancedNotes).forEach(key => {
          advancedNotesCopy[key] = { ...state.advancedNotes[key] };
        });
        state.trackingCache[currentKey] = {
          askedQuestions: [...state.askedQuestions],
          questionNotes: questionNotesCopy,
          checkedPitches: [...state.checkedPitches],
          pitchNotes: pitchNotesCopy,
          checkedAdvanced: [...state.checkedAdvanced],
          advancedNotes: advancedNotesCopy
        };
        console.log('[SalesPlaybook] setSelectedIndustry - Saved to cache:', currentKey);
      }

      if (action.payload) {
        state.selectedIndustry = action.payload.industry;
        state.selectedIndustryDisplayName = action.payload.displayName;
        state.availableRoles = action.payload.roles;

        // Use 'general' role if it exists, otherwise use the first available role
        const generalRole = action.payload.roles.find(r => r.name === 'general');
        const defaultRole = generalRole || action.payload.roles[0];
        state.selectedRole = defaultRole?.name || 'general';
        state.selectedRoleDisplayName = defaultRole?.displayName || 'General';

        // Check if we have cached data for this industry/role combination
        const newKey = getTrackingCacheKey(action.payload.industry, state.selectedRole);
        const cached = state.trackingCache[newKey];
        if (cached) {
          // Restore from cache
          state.askedQuestions = [...(cached.askedQuestions || [])];
          state.checkedPitches = [...(cached.checkedPitches || [])];
          state.checkedAdvanced = [...(cached.checkedAdvanced || [])];
          // Deep copy when restoring
          const restoredQuestionNotes: Record<string, ItemNote> = {};
          Object.keys(cached.questionNotes || {}).forEach(key => {
            restoredQuestionNotes[key] = { ...cached.questionNotes[key] };
          });
          state.questionNotes = restoredQuestionNotes;
          const restoredPitchNotes: Record<string, ItemNote> = {};
          Object.keys(cached.pitchNotes || {}).forEach(key => {
            restoredPitchNotes[key] = { ...cached.pitchNotes[key] };
          });
          state.pitchNotes = restoredPitchNotes;
          const restoredAdvancedNotes: Record<string, ItemNote> = {};
          Object.keys(cached.advancedNotes || {}).forEach(key => {
            restoredAdvancedNotes[key] = { ...cached.advancedNotes[key] };
          });
          state.advancedNotes = restoredAdvancedNotes;
          console.log('[SalesPlaybook] setSelectedIndustry - Restored from cache:', newKey);
        } else {
          // No cache for this combination, start fresh
          state.askedQuestions = [];
          state.questionNotes = {};
          state.checkedPitches = [];
          state.pitchNotes = {};
          state.checkedAdvanced = [];
          state.advancedNotes = {};
          console.log('[SalesPlaybook] setSelectedIndustry - No cache found, starting fresh:', newKey);
        }
      } else {
        state.selectedIndustry = null;
        state.selectedIndustryDisplayName = null;
        state.availableRoles = [];
        state.selectedRole = 'general';
        state.selectedRoleDisplayName = null;
        state.currentPitch = [];
        state.currentAdvanced = [];
        state.questions = [];
        state.askedQuestions = [];
        state.questionNotes = {};
        state.checkedPitches = [];
        state.pitchNotes = {};
        state.checkedAdvanced = [];
        state.advancedNotes = {};
        state.trackingCache = {};
      }
    },

    setSelectedRole: (state, action: PayloadAction<{ name: string; displayName: string }>) => {
      // Save current tracking data to cache before switching role
      if (state.selectedIndustry && state.selectedRole) {
        const currentKey = getTrackingCacheKey(state.selectedIndustry, state.selectedRole);
        // Deep copy all notes to avoid reference issues
        const questionNotesCopy: Record<string, ItemNote> = {};
        Object.keys(state.questionNotes).forEach(key => {
          questionNotesCopy[key] = { ...state.questionNotes[key] };
        });
        const pitchNotesCopy: Record<string, ItemNote> = {};
        Object.keys(state.pitchNotes).forEach(key => {
          pitchNotesCopy[key] = { ...state.pitchNotes[key] };
        });
        const advancedNotesCopy: Record<string, ItemNote> = {};
        Object.keys(state.advancedNotes).forEach(key => {
          advancedNotesCopy[key] = { ...state.advancedNotes[key] };
        });
        state.trackingCache[currentKey] = {
          askedQuestions: [...state.askedQuestions],
          questionNotes: questionNotesCopy,
          checkedPitches: [...state.checkedPitches],
          pitchNotes: pitchNotesCopy,
          checkedAdvanced: [...state.checkedAdvanced],
          advancedNotes: advancedNotesCopy
        };
        console.log('[SalesPlaybook] Saved to cache:', currentKey);
      }

      const previousRole = state.selectedRole;
      state.selectedRole = action.payload.name;
      state.selectedRoleDisplayName = action.payload.displayName;

      // Restore tracking from cache if available
      if (state.selectedIndustry) {
        const newKey = getTrackingCacheKey(state.selectedIndustry, action.payload.name);
        const cached = state.trackingCache[newKey];
        console.log('[SalesPlaybook] Switching from', previousRole, 'to', action.payload.name);
        if (cached) {
          // Deep copy when restoring
          state.askedQuestions = [...(cached.askedQuestions || [])];
          state.checkedPitches = [...(cached.checkedPitches || [])];
          state.checkedAdvanced = [...(cached.checkedAdvanced || [])];
          const restoredQuestionNotes: Record<string, ItemNote> = {};
          Object.keys(cached.questionNotes || {}).forEach(key => {
            restoredQuestionNotes[key] = { ...cached.questionNotes[key] };
          });
          state.questionNotes = restoredQuestionNotes;
          const restoredPitchNotes: Record<string, ItemNote> = {};
          Object.keys(cached.pitchNotes || {}).forEach(key => {
            restoredPitchNotes[key] = { ...cached.pitchNotes[key] };
          });
          state.pitchNotes = restoredPitchNotes;
          const restoredAdvancedNotes: Record<string, ItemNote> = {};
          Object.keys(cached.advancedNotes || {}).forEach(key => {
            restoredAdvancedNotes[key] = { ...cached.advancedNotes[key] };
          });
          state.advancedNotes = restoredAdvancedNotes;
          console.log('[SalesPlaybook] Restored from cache');
        } else {
          state.askedQuestions = [];
          state.questionNotes = {};
          state.checkedPitches = [];
          state.pitchNotes = {};
          state.checkedAdvanced = [];
          state.advancedNotes = {};
          console.log('[SalesPlaybook] No cache found, starting fresh');
        }
      }
    },

    markQuestionAsked: (state, action: PayloadAction<string>) => {
      if (!state.askedQuestions.includes(action.payload)) {
        state.askedQuestions.push(action.payload);
      }
    },

    unmarkQuestionAsked: (state, action: PayloadAction<string>) => {
      state.askedQuestions = state.askedQuestions.filter(id => id !== action.payload);
    },

    toggleQuestionAsked: (state, action: PayloadAction<string>) => {
      const questionId = action.payload;
      if (state.askedQuestions.includes(questionId)) {
        state.askedQuestions = state.askedQuestions.filter(id => id !== questionId);
        console.log('[SalesPlaybook] Unmarked question:', questionId, 'Current role:', state.selectedRole);
      } else {
        state.askedQuestions.push(questionId);
        console.log('[SalesPlaybook] Marked question:', questionId, 'Current role:', state.selectedRole);
      }
      console.log('[SalesPlaybook] askedQuestions now:', state.askedQuestions);
    },

    addQuestionNote: (state, action: PayloadAction<{ questionId: string; note: string }>) => {
      state.questionNotes[action.payload.questionId] = {
        note: action.payload.note,
        timestamp: new Date().toISOString()
      };
      console.log('[SalesPlaybook] Added note for question:', action.payload.questionId, 'Current role:', state.selectedRole);
    },

    removeQuestionNote: (state, action: PayloadAction<string>) => {
      delete state.questionNotes[action.payload];
    },

    // Pitch tracking
    togglePitchChecked: (state, action: PayloadAction<string>) => {
      const pitchId = action.payload;
      if (state.checkedPitches.includes(pitchId)) {
        state.checkedPitches = state.checkedPitches.filter(id => id !== pitchId);
        console.log('[SalesPlaybook] Unchecked pitch:', pitchId);
      } else {
        state.checkedPitches.push(pitchId);
        console.log('[SalesPlaybook] Checked pitch:', pitchId);
      }
    },

    addPitchNote: (state, action: PayloadAction<{ pitchId: string; note: string }>) => {
      state.pitchNotes[action.payload.pitchId] = {
        note: action.payload.note,
        timestamp: new Date().toISOString()
      };
      console.log('[SalesPlaybook] Added note for pitch:', action.payload.pitchId);
    },

    removePitchNote: (state, action: PayloadAction<string>) => {
      delete state.pitchNotes[action.payload];
    },

    // Advanced tracking
    toggleAdvancedChecked: (state, action: PayloadAction<string>) => {
      const advancedId = action.payload;
      if (state.checkedAdvanced.includes(advancedId)) {
        state.checkedAdvanced = state.checkedAdvanced.filter(id => id !== advancedId);
        console.log('[SalesPlaybook] Unchecked advanced:', advancedId);
      } else {
        state.checkedAdvanced.push(advancedId);
        console.log('[SalesPlaybook] Checked advanced:', advancedId);
      }
    },

    addAdvancedNote: (state, action: PayloadAction<{ advancedId: string; note: string }>) => {
      state.advancedNotes[action.payload.advancedId] = {
        note: action.payload.note,
        timestamp: new Date().toISOString()
      };
      console.log('[SalesPlaybook] Added note for advanced:', action.payload.advancedId);
    },

    removeAdvancedNote: (state, action: PayloadAction<string>) => {
      delete state.advancedNotes[action.payload];
    },

    clearSalesPlaybookSession: (state) => {
      state.selectedIndustry = null;
      state.selectedIndustryDisplayName = null;
      state.selectedRole = 'general';
      state.selectedRoleDisplayName = null;
      state.availableRoles = [];
      state.currentPitch = [];
      state.currentAdvanced = [];
      state.questions = [];
      state.askedQuestions = [];
      state.questionNotes = {};
      state.checkedPitches = [];
      state.pitchNotes = {};
      state.checkedAdvanced = [];
      state.advancedNotes = {};
      state.trackingCache = {}; // Clear the per-selection cache
      state.error = null;
    },

    clearError: (state) => {
      state.error = null;
    },

    restoreSalesPlaybookState: (state, action: PayloadAction<{
      selectedIndustry: string;
      selectedIndustryDisplayName: string;
      selectedRole: string;
      selectedRoleDisplayName: string;
      availableRoles: SalesPlaybookRoleInfo[];
      askedQuestions: string[];
      questionNotes: Record<string, { note: string; timestamp: string }>;
      checkedPitches?: string[];
      pitchNotes?: Record<string, { note: string; timestamp: string }>;
      checkedAdvanced?: string[];
      advancedNotes?: Record<string, { note: string; timestamp: string }>;
      trackingCache?: Record<string, SelectionTrackingData>;
    }>) => {
      state.selectedIndustry = action.payload.selectedIndustry;
      state.selectedIndustryDisplayName = action.payload.selectedIndustryDisplayName;
      state.selectedRole = action.payload.selectedRole;
      state.selectedRoleDisplayName = action.payload.selectedRoleDisplayName;
      state.availableRoles = action.payload.availableRoles;
      state.askedQuestions = action.payload.askedQuestions;
      state.questionNotes = action.payload.questionNotes;
      state.checkedPitches = action.payload.checkedPitches || [];
      state.pitchNotes = action.payload.pitchNotes || {};
      state.checkedAdvanced = action.payload.checkedAdvanced || [];
      state.advancedNotes = action.payload.advancedNotes || {};

      // Restore tracking cache if provided, otherwise populate with current selection's data
      if (action.payload.trackingCache) {
        state.trackingCache = action.payload.trackingCache;
      } else {
        // At minimum, cache the current selection's data
        const key = getTrackingCacheKey(action.payload.selectedIndustry, action.payload.selectedRole);
        state.trackingCache[key] = {
          askedQuestions: action.payload.askedQuestions,
          questionNotes: action.payload.questionNotes,
          checkedPitches: action.payload.checkedPitches || [],
          pitchNotes: action.payload.pitchNotes || {},
          checkedAdvanced: action.payload.checkedAdvanced || [],
          advancedNotes: action.payload.advancedNotes || {}
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch industries
      .addCase(fetchIndustries.pending, (state) => {
        state.industriesLoading = true;
        state.error = null;
      })
      .addCase(fetchIndustries.fulfilled, (state, action) => {
        state.industriesLoading = false;
        state.industries = action.payload || [];
      })
      .addCase(fetchIndustries.rejected, (state, action) => {
        state.industriesLoading = false;
        state.error = action.payload as string;
      })
      // Fetch role data
      .addCase(fetchRoleData.pending, (state) => {
        state.dataLoading = true;
        state.error = null;
      })
      .addCase(fetchRoleData.fulfilled, (state, action) => {
        state.dataLoading = false;
        if (action.payload) {
          state.currentPitch = action.payload.pitch || [];
          state.currentAdvanced = action.payload.advanced || [];
          state.questions = action.payload.questions || [];
        }
      })
      .addCase(fetchRoleData.rejected, (state, action) => {
        state.dataLoading = false;
        state.error = action.payload as string;
        state.currentPitch = [];
        state.currentAdvanced = [];
        state.questions = [];
      });
  }
});

export const {
  setSelectedIndustry,
  setSelectedRole,
  markQuestionAsked,
  unmarkQuestionAsked,
  toggleQuestionAsked,
  addQuestionNote,
  removeQuestionNote,
  togglePitchChecked,
  addPitchNote,
  removePitchNote,
  toggleAdvancedChecked,
  addAdvancedNote,
  removeAdvancedNote,
  clearSalesPlaybookSession,
  clearError,
  restoreSalesPlaybookState
} = salesPlaybookSlice.actions;

// Backward compatibility aliases
export const clearTalkTracksSession = clearSalesPlaybookSession;
export const restoreTalkTrackState = restoreSalesPlaybookState;

// Type aliases for backward compatibility
export type TalkTrackTag = SalesPlaybookTag;
export type TalkTrackQuestion = SalesPlaybookQuestion;
export type TalkTrackRoleInfo = SalesPlaybookRoleInfo;
export type TalkTrackIndustryInfo = SalesPlaybookIndustryInfo;

export default salesPlaybookSlice.reducer;
